import type { ProjectListItem, ProjectStatusEnum } from "@nlc/shared-types";
import type { ColumnDef } from "@tanstack/react-table";
import type { SortDescriptor } from "react-stately";

import { ProjectGroupEnum, USER_ROLES } from "@nlc/shared-types";
import { Link } from "@tanstack/react-router";
import { memo, useCallback, useEffect, useMemo, useRef } from "react";

import {
  type PaginationMetadata,
  DraggableTable as Table,
} from "@/components/table";
import { ProjectStatusBadge } from "@/features/shared/components/ProjectStatusBadge";
import { useTableSorting } from "@/hooks/useTableSorting";
import { useUser } from "@/hooks/useUser";
import { dateFormatterWithConditionalYear } from "@/utils/dateFormatterWithConditionalYear";

import { COLUMN_WIDTHS } from "./utils/columnWidths";

interface ColumnConfig {
  always?: boolean;
  excludeForRoles?: string[];
  groups?: string[]; // ProjectGroupEnum values: "active", "cancelled", "archived", "all"
}

const COLUMN_CONFIGS: Record<string, ColumnConfig> = {
  cancelDate: {
    excludeForRoles: [USER_ROLES.DESIGNER],
    groups: [
      ProjectGroupEnum.ACTIVE,
      ProjectGroupEnum.CANCELLED,
      ProjectGroupEnum.ALL,
    ],
  },
  createdAt: {
    groups: [
      ProjectGroupEnum.ACTIVE,
      ProjectGroupEnum.CANCELLED,
      ProjectGroupEnum.ALL,
      ProjectGroupEnum.KICKSTARTER,
    ],
  },
  customerSuccess: {
    excludeForRoles: [USER_ROLES.DESIGNER],
    groups: [ProjectGroupEnum.ACTIVE],
  },
  name: { always: true },
  openDrafts: {
    groups: [ProjectGroupEnum.KICKSTARTER],
  },
  openRequests: {
    groups: [
      ProjectGroupEnum.ACTIVE,
      ProjectGroupEnum.CANCELLED,
      ProjectGroupEnum.ALL,
    ],
  },
  owner: {
    groups: [
      ProjectGroupEnum.ACTIVE,
      ProjectGroupEnum.CANCELLED,
      ProjectGroupEnum.ARCHIVED,
      ProjectGroupEnum.ALL,
      ProjectGroupEnum.KICKSTARTER,
    ],
  },
  ownerEmail: {
    groups: [ProjectGroupEnum.KICKSTARTER],
  },
  product: {
    groups: [
      ProjectGroupEnum.ACTIVE,
      ProjectGroupEnum.CANCELLED,
      ProjectGroupEnum.ARCHIVED,
      ProjectGroupEnum.ALL,
    ],
  },
  renewalDate: {
    excludeForRoles: [USER_ROLES.DESIGNER],
    groups: [ProjectGroupEnum.ACTIVE, ProjectGroupEnum.ALL],
  },
  status: {
    groups: [
      ProjectGroupEnum.ACTIVE,
      ProjectGroupEnum.ALL,
      ProjectGroupEnum.KICKSTARTER,
    ],
  },
};

// Utility function
const shouldShowColumn = (
  columnKey: string,
  group: string, // e.g. "active"
  userRole?: null | string,
): boolean => {
  const config = COLUMN_CONFIGS[columnKey];

  if (!config) return false;

  // Role-specific exclusions
  if (userRole && config.excludeForRoles?.includes(userRole)) {
    return false;
  }

  return config.always || (config.groups?.includes(group) ?? false);
};

interface ProjectsTableProps {
  currentGroup: string;
  isLoading?: boolean;
  pagination?: {
    metadata: PaginationMetadata;
    onItemsPerPageChange?: (itemsPerPage: number) => void;
    onPageChange: (page: number) => void;
    pageSizeOptions?: number[];
    showPageSizeSelector?: boolean;
  };
  projects: ProjectListItem[];
  setSortDescriptor: (sortDescriptor: SortDescriptor) => void;
  sortDescriptor: SortDescriptor;
}

const LinkCell = memo(function LinkCell({
  href,
  name,
}: {
  href: string;
  name: string;
}) {
  return (
    <Link
      className="font-medium text-text-primary hover:text-text-brand-secondary line-clamp-3"
      to={href}
    >
      {name}
    </Link>
  );
});

const DefaultCell = memo(function DefaultCell({ value }: { value: unknown }) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden h-16 flex items-center">
      <div className="text-text-tertiary text-sm leading-tight line-clamp-3">
        {(value as string) || "-"}
      </div>
    </div>
  );
});

const StatusCell = memo(function StatusCell({
  status,
}: {
  status: ProjectStatusEnum;
}) {
  return (
    <div className="py-3 pr-3 min-w-0 w-full overflow-hidden h-16 flex items-center">
      <ProjectStatusBadge status={status} />
    </div>
  );
});

const BoldCell = memo(function DefaultCell({ value }: { value: unknown }) {
  return (
    <span className="text-sm font-medium text-text-primary">
      {(value as string) || "-"}
    </span>
  );
});

export const ProjectsTable = memo(function ProjectsTable({
  currentGroup,
  isLoading = false,
  pagination,
  projects,
  setSortDescriptor,
  sortDescriptor,
}: ProjectsTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  const { data: userData } = useUser();
  const currentUserRole = userData?.user?.roleName;

  const { handleSort, sorting } = useTableSorting({
    defaultColumn: "createdAt",
    defaultDirection: "descending",
    setSortDescriptor,
    sortDescriptor,
  });

  useEffect(() => {
    if (tableRef.current) {
      const table = tableRef.current.querySelector("table");

      if (table) {
        table.style.tableLayout = "fixed";
        table.style.width = "100%";
      }

      const cells = tableRef.current.querySelectorAll("td");

      for (const cell of cells) {
        cell.style.overflow = "hidden";
        cell.style.maxWidth = "0";
      }
    }
  }, [projects]);

  const columns: ColumnDef<ProjectListItem>[] = useMemo(() => {
    const columns: ColumnDef<ProjectListItem>[] = [];

    if (shouldShowColumn("name", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "name",
        cell: ({ getValue }) => {
          const nameObj = getValue() as { id: number; name: string };

          return (
            <LinkCell href={`/projects/${nameObj?.id}`} name={nameObj?.name} />
          );
        },
        enableSorting: true,
        header: "Name",
        maxSize: COLUMN_WIDTHS.NAME,
        minSize: COLUMN_WIDTHS.NAME,
        size: COLUMN_WIDTHS.NAME,
      });
    }

    if (shouldShowColumn("owner", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "owner.name",
        cell: ({ row }) => {
          const ownerObj = row.original.owner;

          return (
            <LinkCell
              href={ownerObj ? `/users/${ownerObj.id}/edit` : "#"}
              name={ownerObj?.name || "-"}
            />
          );
        },
        enableSorting: true,
        header: "Owner",
        id: "owner.name",
        maxSize: COLUMN_WIDTHS.OWNER,
        minSize: COLUMN_WIDTHS.OWNER,
        size: COLUMN_WIDTHS.OWNER,
      });
    }

    if (shouldShowColumn("ownerEmail", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "owner.email",
        cell: ({ row }) => {
          const ownerObj = row.original.owner;

          return <DefaultCell value={ownerObj?.email} />;
        },
        enableSorting: true,
        header: "Owner Email",
        id: "owner.email",
        maxSize: COLUMN_WIDTHS.OWNER,
        minSize: COLUMN_WIDTHS.OWNER,
        size: COLUMN_WIDTHS.OWNER,
      });
    }

    if (shouldShowColumn("renewalDate", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "renewalDate",
        cell: ({ getValue }) => (
          <BoldCell
            value={dateFormatterWithConditionalYear(getValue() as Date)}
          />
        ),
        enableSorting: true,
        header: "Renewal date",
        maxSize: COLUMN_WIDTHS.RENEWAL_DATE,
        minSize: COLUMN_WIDTHS.RENEWAL_DATE,
        size: COLUMN_WIDTHS.RENEWAL_DATE,
      });
    }

    if (shouldShowColumn("product", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "product",
        cell: ({ getValue }) => {
          const productObj = getValue() as { id: number; name: string } | null;

          return <BoldCell value={productObj?.name || "-"} />;
        },
        enableSorting: true,
        header: "Product",
        maxSize: COLUMN_WIDTHS.PRODUCT,
        minSize: COLUMN_WIDTHS.PRODUCT,
        size: COLUMN_WIDTHS.PRODUCT,
      });
    }

    if (shouldShowColumn("customerSuccess", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "customerSuccess",
        cell: ({ getValue }) => {
          const customerSuccessObj = getValue() as {
            id: number;
            name: string;
          } | null;

          return (
            <LinkCell
              href={
                customerSuccessObj
                  ? `/users/${customerSuccessObj.id}/edit`
                  : "#"
              }
              name={customerSuccessObj?.name || "-"}
            />
          );
        },
        enableSorting: true,
        header: "CSR",
        maxSize: COLUMN_WIDTHS.CUSTOMER_SUCCESS,
        minSize: COLUMN_WIDTHS.CUSTOMER_SUCCESS,
        size: COLUMN_WIDTHS.CUSTOMER_SUCCESS,
      });
    }

    if (shouldShowColumn("openDrafts", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "openDrafts",
        cell: ({ getValue }) => <BoldCell value={getValue()} />,
        enableSorting: true,
        header: "Open drafts",
        maxSize: COLUMN_WIDTHS.OPEN_REQUESTS,
        minSize: COLUMN_WIDTHS.OPEN_REQUESTS,
        size: COLUMN_WIDTHS.OPEN_REQUESTS,
      });
    }
    if (shouldShowColumn("openRequests", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "openRequests",
        cell: ({ getValue }) => <BoldCell value={getValue()} />,
        enableSorting: true,
        header: "Open requests",
        maxSize: COLUMN_WIDTHS.OPEN_REQUESTS,
        minSize: COLUMN_WIDTHS.OPEN_REQUESTS,
        size: COLUMN_WIDTHS.OPEN_REQUESTS,
      });
    }

    if (shouldShowColumn("status", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "status",
        cell: ({ getValue }) => (
          <StatusCell status={getValue() as ProjectStatusEnum} />
        ),
        enableSorting: true,
        header: "Status",
        maxSize: COLUMN_WIDTHS.STATUS,
        minSize: COLUMN_WIDTHS.STATUS,
        size: COLUMN_WIDTHS.STATUS,
      });
    }

    if (shouldShowColumn("createdAt", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "createdAt",
        cell: ({ getValue }) => (
          <DefaultCell
            value={dateFormatterWithConditionalYear(getValue() as Date)}
          />
        ),
        enableSorting: true,
        header: "Created on",
        maxSize: COLUMN_WIDTHS.CREATED_AT,
        minSize: COLUMN_WIDTHS.CREATED_AT,
        size: COLUMN_WIDTHS.CREATED_AT,
      });
    }

    if (shouldShowColumn("cancelDate", currentGroup, currentUserRole)) {
      columns.push({
        accessorKey: "cancelDate",
        cell: ({ getValue }) => (
          <DefaultCell
            value={dateFormatterWithConditionalYear(getValue() as Date)}
          />
        ),
        enableSorting: true,
        header: "Cancel date",
        maxSize: COLUMN_WIDTHS.CANCEL_DATE,
        minSize: COLUMN_WIDTHS.CANCEL_DATE,
        size: COLUMN_WIDTHS.CANCEL_DATE,
      });
    }

    return columns;
  }, [currentGroup, currentUserRole]);

  const getRowId = useCallback((project: ProjectListItem) => project.id, []);

  const paginationConfig = useMemo(
    () =>
      pagination
        ? {
            metadata: pagination.metadata,
            onItemsPerPageChange: pagination.onItemsPerPageChange,
            onPageChange: pagination.onPageChange,
            pageSizeOptions: pagination.pageSizeOptions,
            showPageSizeSelector: pagination.showPageSizeSelector,
          }
        : undefined,
    [
      pagination?.metadata,
      pagination?.onItemsPerPageChange,
      pagination?.onPageChange,
      pagination?.pageSizeOptions,
      pagination?.showPageSizeSelector,
    ],
  );

  const sortableColumns = useMemo(() => {
    const sortableColumns: string[] = [];

    if (shouldShowColumn("name", currentGroup, currentUserRole)) {
      sortableColumns.push("name");
    }
    if (shouldShowColumn("owner", currentGroup, currentUserRole)) {
      sortableColumns.push("owner.name");
    }
    if (shouldShowColumn("ownerEmail", currentGroup, currentUserRole)) {
      sortableColumns.push("owner.email");
    }
    if (shouldShowColumn("openDrafts", currentGroup, currentUserRole)) {
      sortableColumns.push("openDrafts");
    }
    if (shouldShowColumn("renewalDate", currentGroup, currentUserRole)) {
      sortableColumns.push("renewalDate");
    }
    if (shouldShowColumn("product", currentGroup, currentUserRole)) {
      sortableColumns.push("product");
    }
    if (shouldShowColumn("customerSuccess", currentGroup, currentUserRole)) {
      sortableColumns.push("customerSuccess");
    }
    if (shouldShowColumn("openRequests", currentGroup, currentUserRole)) {
      sortableColumns.push("openRequests");
    }
    if (shouldShowColumn("status", currentGroup, currentUserRole)) {
      sortableColumns.push("status");
    }
    if (shouldShowColumn("cancelDate", currentGroup, currentUserRole)) {
      sortableColumns.push("cancelDate");
    }
    if (shouldShowColumn("createdAt", currentGroup, currentUserRole)) {
      sortableColumns.push("createdAt");
    }

    return sortableColumns;
  }, [currentGroup, currentUserRole]);

  return (
    <div className="rounded-b-xl bg-bg-primary overflow-auto" ref={tableRef}>
      <Table
        className="rounded-b-xl brands-table w-full"
        columns={columns}
        data={projects}
        getRowId={getRowId}
        isLoading={isLoading}
        isServerSideSorting
        onSort={handleSort}
        pagination={paginationConfig}
        sortableColumns={sortableColumns}
        sorting={sorting}
      />
    </div>
  );
});
